import { forwardRef, useCallback, useState } from "react";
import { Loader, Notification, useToaster } from "rsuite";
import { openReportWithAuth } from "@/api/util";

interface ReportLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  url: string;
  children: React.ReactNode;
  /**
   * Optional guard run before the report is fetched. Return false (or a
   * promise resolving to false) to abort without entering the loading state —
   * e.g. to block generation and show a message.
   */
  onBeforeOpen?: () => boolean | Promise<boolean>;
}

export const ReportLink = forwardRef<HTMLAnchorElement, ReportLinkProps>(
  ({ url, children, onClick, onBeforeOpen, style, ...rest }, ref) => {
    const toaster = useToaster();
    const [loading, setLoading] = useState(false);

    const handleClick = useCallback(
      async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (loading) return;
        onClick?.(e);

        if (onBeforeOpen) {
          const proceed = await onBeforeOpen();
          if (!proceed) return;
        }

        setLoading(true);
        try {
          await openReportWithAuth(url);
        } catch (error: unknown) {
          const message =
            error && typeof error === "object" && "message" in error
              ? String((error as { message: unknown }).message)
              : "Kunne ikke hente rapporten.";
          toaster.push(
            <Notification type="error" header="Fejl">
              {message}
            </Notification>,
            { placement: "topEnd", duration: 5000 },
          );
        } finally {
          setLoading(false);
        }
      },
      [url, onClick, onBeforeOpen, toaster, loading],
    );

    return (
      <a
        ref={ref}
        href={url}
        onClick={handleClick}
        aria-busy={loading}
        aria-disabled={loading}
        style={{
          ...style,
          ...(loading
            ? { pointerEvents: "none", opacity: 0.6, cursor: "progress" }
            : {}),
        }}
        {...rest}
      >
        {children}
        {loading && (
          <Loader size="xs" style={{ marginLeft: "auto", paddingLeft: 8 }} />
        )}
      </a>
    );
  },
);

ReportLink.displayName = "ReportLink";
